import { nextTick, onScopeDispose, watch, type Ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';

/**
 * Publishes the docked audio player's live height as a CSS variable on <html>,
 * so any full-viewport overlay can end ABOVE the player instead of running
 * underneath it.
 *
 * Why a variable rather than a constant: the bar is not one height. It is ~70px
 * with a plain scrubber, ~89px once the waveform and duration row are in, and
 * taller again when it wraps or shows its "Request failed" line. Anything
 * hardcoded would be wrong in most of those states.
 *
 * Why this matters: on a phone the bar covers the bottom of the screen, and
 * every full-viewport surface was exactly `100dvh` tall — the center peek the
 * inspector had then, the full-screen Columns / Filter / Saved-view sheets,
 * the command picker. So the bottom ~89px of each one sat under the player. In
 * that center peek the player was lifted to `z-[60]` and painted straight over
 * the content, which is how Column Settings' Save and Cancel buttons became
 * unreachable. In the sheets the stacking is the other way round and the
 * player itself was buried instead. Scrolling did not rescue either case: the
 * scroll container's own box ended under the bar, so its last rows could never
 * come out from behind it.
 *
 * Consumers use `h-[calc(100dvh-var(--global-audio-player-height,0px))]`. The
 * `0px` fallback is what makes this safe: with nothing playing the variable is
 * 0 and every surface is full height exactly as before.
 *
 * TWO update paths, deliberately. `isVisible` is the authoritative one in BOTH
 * directions: showing re-measures on the next tick, hiding publishes 0 outright.
 * The ResizeObserver only catches later changes to an already-visible bar (a
 * wrap, the error line, a rotate). Relying on the observer alone is not safe —
 * it delivers on the browser's rendering steps, which a throttled or background
 * tab may not run, and vueuse makes the whole block a no-op where
 * ResizeObserver is unsupported. Either way the reservation would sit at a
 * stale value with the player in the opposite state.
 */
export const PLAYER_HEIGHT_VAR = '--global-audio-player-height';

export function usePublishedPlayerHeight(
  elementRef: Ref<HTMLElement | null>,
  isVisible: Ref<boolean>,
): void {
  function publish(px: number): void {
    document.documentElement.style.setProperty(PLAYER_HEIGHT_VAR, `${Math.round(px)}px`);
  }

  function measureAndPublish(): void {
    const element = elementRef.value;
    publish(element === null ? 0 : element.getBoundingClientRect().height);
  }

  watch(
    [isVisible, elementRef],
    async () => {
      // Hiding publishes 0 WITHOUT measuring. The bar leaves on a transform
      // (`translate-y-full`), so it stays in normal flow for the whole 150ms
      // and the wrapper still measures its full height on the next tick — we
      // would publish ~89px for a player that is on its way off screen, and
      // only the ResizeObserver would ever correct it. That is precisely the
      // dependency this watcher exists to avoid: if the observation never lands
      // (throttled tab, or no ResizeObserver at all) the reservation would stay
      // at 89px forever and every mobile overlay would be short by that much
      // with no player on screen.
      if (!isVisible.value) {
        publish(0);
        return;
      }

      // The bar mounts inside the wrapper on this tick; measure on the next one
      // or we would read the pre-mount height of 0.
      await nextTick();
      measureAndPublish();
    },
    { immediate: true },
  );

  useResizeObserver(elementRef, (entries) => {
    const entry = entries[0];
    if (!entry) {
      return;
    }
    // borderBoxSize is the honest number for a bordered bar; contentRect is the
    // fallback for engines that do not report it.
    publish(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
  });

  // The layout owning this outlives every overlay, but if it is ever torn down
  // (logout drops SidebarLayout) a stale reservation would shrink overlays that
  // no longer have a player under them.
  onScopeDispose(() => {
    document.documentElement.style.removeProperty(PLAYER_HEIGHT_VAR);
  });
}
